import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Add as AddIcon,
  EventAvailable as EventAvailableIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { ptoBalancesApi, ptoReportsApi } from '../services/api';
import { PTORequestStatus } from '../types';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const MyPTO: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const currentYear = new Date().getFullYear();

  // Fetch balances
  const { data: balancesData, isLoading: balancesLoading, error: balancesError } = useQuery(
    ['myBalances', currentYear],
    () => ptoBalancesApi.getMyBalances(currentYear)
  );

  // Fetch history
  const { data: historyData, isLoading: historyLoading, error: historyError } = useQuery(
    ['myPTOHistory', currentYear],
    () => ptoReportsApi.getMyHistory(currentYear)
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: PTORequestStatus): "default" | "warning" | "success" | "error" => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'denied':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
  };

  const formatPTOType = (type: string) => {
    return type.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (balancesLoading || historyLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (balancesError || historyError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error loading PTO information. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          My PTO
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/pto/request')}
        >
          Request Time Off
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab icon={<EventAvailableIcon />} label="Balances" iconPosition="start" />
        <Tab icon={<HistoryIcon />} label="Request History" iconPosition="start" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {/* Balances Tab */}
        <Grid container spacing={3}>
          {balancesData?.balances && balancesData.balances.length > 0 ? (
            balancesData.balances.map((balance) => (
              <Grid item xs={12} md={6} lg={4} key={balance.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {balance.policy.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      {formatPTOType(balance.policy.pto_type)}
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Available
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {(balance.current_balance || 0).toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            days
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Used
                          </Typography>
                          <Typography variant="h4">
                            {(balance.used_balance || 0).toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            days
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Accrued:
                            </Typography>
                            <Typography variant="body2">
                              {(balance.accrued_balance || 0).toFixed(1)} days
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Pending:
                            </Typography>
                            <Typography variant="body2">
                              {(balance.pending_balance || 0).toFixed(1)} days
                            </Typography>
                          </Grid>
                          {(balance.carryover_balance || 0) > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">
                                Carryover:
                              </Typography>
                              <Typography variant="body2">
                                {(balance.carryover_balance || 0).toFixed(1)} days
                                {balance.carryover_expiry_date && (
                                  <Typography variant="caption" color="error" display="block">
                                    Expires: {formatDate(balance.carryover_expiry_date)}
                                  </Typography>
                                )}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                No PTO balances found for {currentYear}. Please contact your manager.
              </Alert>
            </Grid>
          )}
        </Grid>

        {/* Summary Stats */}
        {historyData && (
          <Paper sx={{ mt: 3, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {currentYear} Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Requests
                </Typography>
                <Typography variant="h5">
                  {historyData.total_requests || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Approved Days
                </Typography>
                <Typography variant="h5">
                  {(historyData.approved_days || 0).toFixed(1)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Pending Days
                </Typography>
                <Typography variant="h5">
                  {(historyData.pending_days || 0).toFixed(1)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Request History Tab */}
        {historyData?.requests && historyData.requests.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Reviewed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.requests.map((request: any) => (
                  <TableRow key={request.id} hover>
                    <TableCell>{formatPTOType(request.pto_type)}</TableCell>
                    <TableCell>{formatDate(request.start_date)}</TableCell>
                    <TableCell>{formatDate(request.end_date)}</TableCell>
                    <TableCell align="right">{(request.total_days || 0).toFixed(1)}</TableCell>
                    <TableCell>
                      <Chip
                        label={request.status.toUpperCase()}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(request.created_at)}</TableCell>
                    <TableCell>
                      {request.reviewed_at ? formatDate(request.reviewed_at) : '-'}
                      {request.review_notes && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {request.review_notes}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No PTO requests found for {currentYear}.
          </Alert>
        )}
      </TabPanel>
    </Container>
  );
};

export default MyPTO;
